import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { audiencesApi } from '../../../api/audiences';
import Trash from '../../../assets/icons/trash.svg';
import Spinner from '../../../components/base/Spinner';
import {
  getUserSegment,
  setFormIsReady,
} from '../../../store/reducers/segment';
import { convertToReadableFormat } from '../../../tools/NumberConverterTool';
import useCancelableRequest from '../../../utils/cancelAPI/useCancelableRequest';
import { shouldSkipWalletFetch } from '../../../utils/segments';
import { placeholderStyle } from '../Condition/Condition.constants';
import styles from './ConditionForm.module.scss';

const ConditionForm = ({
  conditionName,
  edit,
  data,
  content,
  deleteCondition,
  firstSelectOptions,
  defineFirstSelectOptions,
  showAddresses,
  walletCountBody,
  page = 'add-segment',
  totalWalletReach = null,
  isWalletTotalReachLoading = false,
}) => {
  const dispatch = useDispatch();
  const [profileCount, setProfileCount] = useState(null);
  const [profileCountLoading, setProfileCountLoading] = useState(false);
  const prevWalletCountBody = useRef(walletCountBody);

  const segment = useSelector(getUserSegment);
  const conditionCount = segment.segment?.categories?.reduce(
    (sum, category) => sum + category.filters.length,
    0
  );

  useEffect(() => {
    if (
      JSON.stringify(prevWalletCountBody.current) !==
      JSON.stringify(walletCountBody)
    ) {
      prevWalletCountBody.current = walletCountBody;
      setProfileCount(null);
    }
  }, [prevWalletCountBody, walletCountBody]);

  const { data: totalWalletCount, isLoading: isTotalWalletCountLoading } =
    useCancelableRequest({
      endpoint: audiencesApi.endpoints.getSegmentWalletsCount,
      params: { body: walletCountBody },
      shouldSkip:
        profileCount !== null ||
        conditionCount === 1 ||
        shouldSkipWalletFetch(walletCountBody),
    });

  useEffect(() => {
    if (profileCount === null && showAddresses) {
      if (conditionCount === 1) {
        if (isWalletTotalReachLoading) {
          setProfileCountLoading(true);
        } else {
          setProfileCountLoading(false);
          setProfileCount(totalWalletReach?.profile_count);
        }
      } else if (isTotalWalletCountLoading) {
        setProfileCountLoading(true);
      } else {
        setProfileCountLoading(false);
        setProfileCount(totalWalletCount?.profile_count);
      }
    }
  }, [
    conditionCount,
    isTotalWalletCountLoading,
    isWalletTotalReachLoading,
    profileCount,
    showAddresses,
    totalWalletCount?.profile_count,
    totalWalletReach?.profile_count,
  ]);

  useEffect(() => {
    if (isTotalWalletCountLoading || totalWalletCount) {
      dispatch(setFormIsReady(true));
    }
  }, [dispatch, isTotalWalletCountLoading, totalWalletCount]);

  useEffect(() => {
    if (firstSelectOptions && firstSelectOptions.length === 1) {
      defineFirstSelectOptions(firstSelectOptions[0].value, true);
    }
  }, [defineFirstSelectOptions, firstSelectOptions]);

  return useMemo(() => {
    if (page === 'add-segment') {
      return (
        <div className={styles.wrapper}>
          <div className="d-flex align-items-center justify-content-between">
            <div className="text-md-bold">{conditionName}</div>
            {edit && (
              <Trash
                className="cursor-pointer position-relative"
                onClick={() => {
                  deleteCondition();
                }}
              />
            )}
          </div>
          <div className={`${styles.adaptive} align-items-end gap-3 mt-2`}>
            {firstSelectOptions && (
              <div className="w-100">
                <Select
                  styles={placeholderStyle}
                  maxMenuHeight={194}
                  defaultValue=""
                  options={[...firstSelectOptions]}
                  onChange={(val) => {
                    defineFirstSelectOptions(val.value);
                  }}
                  isDisabled={!edit}
                  value={
                    firstSelectOptions.filter(
                      (opt) => opt.value === data?.name
                    )[0] || ''
                  }
                />
              </div>
            )}
            {content}
          </div>
          <div className={`ms-auto text-md-regular mt-2 ${styles.addresses}`}>
            {profileCountLoading ? (
              <Spinner />
            ) : profileCount ? (
              <>
                {convertToReadableFormat(profileCount)}
                {profileCount === 1 ? ' profile' : ' profiles'}
              </>
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column gap-2">
        <div className="d-flex align-items-center justify-content-between">
          <div className="title-h5-bold">{conditionName}</div>
          {edit && (
            <Trash
              className="cursor-pointer position-relative"
              onClick={() => {
                deleteCondition();
              }}
            />
          )}
        </div>
        {firstSelectOptions && (
          <div className="w-100">
            <Select
              styles={placeholderStyle}
              maxMenuHeight={194}
              defaultValue=""
              options={[...firstSelectOptions]}
              onChange={(val) => {
                defineFirstSelectOptions(val.value);
              }}
              isDisabled={!edit}
              value={
                firstSelectOptions.filter(
                  (opt) => opt.value === data?.name
                )[0] || ''
              }
            />
          </div>
        )}
        {content}
        <div className={`ms-auto text-md-regular mt-2 ${styles.addresses}`}>
          {profileCountLoading ? (
            <Spinner />
          ) : profileCount ? (
            <>
              {convertToReadableFormat(profileCount)}
              {profileCount === 1 ? ' profile' : ' profiles'}
            </>
          ) : null}
        </div>
      </div>
    );
  }, [
    page,
    conditionName,
    edit,
    firstSelectOptions,
    content,
    profileCountLoading,
    profileCount,
    deleteCondition,
    defineFirstSelectOptions,
    data?.name,
  ]);
};

export default ConditionForm;
